import { API, Auth, graphqlOperation } from "aws-amplify";
import axios from "axios";
import moment from "moment";
import toast from "react-hot-toast";
import { signOut } from "../graphql/mutations";
import envFile from "../envFile";
import Compressor from "compressorjs";

export function getId() {
  return Math.floor(Math.random() * 1000000000) + Math.floor(Math.random() * 10000000);
}

export function getDateFormat(d) {
  return d.getFullYear() + "-" + (d.getMonth() + 1 + "").padStart(2, "0") + "-" + (d.getDate() + "").padStart(2, "0");
}

// ACCESS_TOKEN: mint only via the server-side /token broker — the bundled client secret is gone. One retry absorbs a transient blip, then throw so the call site can surface its own message.
export const accessToken = async () => {
  for (let attempt = 1; attempt <= 2; attempt++) {
    try {
      const res = await axios.post(`${envFile.PUBLIC_API_LINK}/token`, {});
      if (res?.data?.access_token) return res;
      console.log(`[accessToken] /token returned no access_token (attempt ${attempt}/2)`);
    } catch (err) {
      console.log(`[accessToken] /token failed (attempt ${attempt}/2)`, err?.message);
    }
  }
  throw new Error("Unable to obtain access token");
};

export const getRelevantLease = (leases) => {
  const currentDate = moment();
  let activeLease = null;
  let nextLease = null;
  let lastLease = null;

  leases?.forEach((lease) => {
    const leaseStartDate = moment(lease.lease_start);
    // Use a direct check on lease.lease_end to decide if moment conversion is necessary
    const leaseEndDate = lease.lease_end ? moment(lease.lease_end) : null;

    // Priority 1: Active lease
    if (leaseStartDate.isSameOrBefore(currentDate) && (!lease.lease_end || leaseEndDate?.isSameOrAfter(currentDate))) {
      activeLease = lease;
    }

    // Priority 2: Future lease
    if (leaseStartDate.isAfter(currentDate) && (!nextLease || leaseStartDate.isBefore(moment(nextLease.lease_start)))) {
      nextLease = lease;
    }

    // Priority 3: Most recent past lease
    if (
      leaseEndDate &&
      leaseEndDate.isBefore(currentDate) &&
      (!lastLease || leaseEndDate.isAfter(moment(lastLease.lease_end)))
    ) {
      lastLease = lease;
    }
  });

  // Determine which lease to return based on the conditions
  const relevantLease = activeLease || nextLease || lastLease;
  return relevantLease ? [relevantLease] : []; // Return in array format
};

export function formatPhoneNumber(text) {
  if (!text) return;
  // Remove all non-digit characters
  text = text?.replace(/\D/g, "");

  // Format based on the number of digits
  if (text.length === 10) {
    return `(${text.slice(0, 3)}) ${text.slice(3, 6)}-${text.slice(6)}`;
  } else {
    text = text.trim();
    text = text.split("(").join("").split(")").join("").split("-").join("");
    if (text?.length >= 1) {
      text = "(" + text;
    }
    if (text?.length > 4) {
      text = text.slice(0, 4) + ")" + text.slice(4);
    }
    if (text?.length > 9) {
      text = text.slice(0, 9) + "-" + text.slice(9);
    }
    return text;
  }
}
export function formatNumber(n) {
  const s = n + "";
  let r = s.split("").reverse();
  const l = r?.length;
  for (let i = 1; i < l / 3; i++) {
    r.splice(i * 3 + i - 1, 0, ",");
  }
  return r.reverse().join("");
}

export const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const logOut = async (reload = true) => { // reload=false keeps an on-screen toast (same contract as the customer portal's logOut)
  
    // ARR-1350 - New code block to better handle sign out errors presented
    try {
      const userData = await Auth.currentAuthenticatedUser();
  
      if (!userData || !userData.signInUserSession || !userData.pool) {
        throw new Error("Your session has expired. Please sign in again.");
      }
      const refreshToken = userData.signInUserSession.refreshToken.token;
      const clientId = userData.pool.clientId;
  
      // If the refreshToken or clientId is missing, handle it silently by continuing to the sign-out process
      if (refreshToken && clientId) {
        try {
          await API.graphql(
            graphqlOperation(signOut, {
              clientId: clientId,
              token: refreshToken,
            })
          );
        } catch (revokeErr) {
          console.error("Token revoke failed, continuing sign-out:", revokeErr); // best-effort: a failed server-side revoke must not block the local sign-out
        }
      }
      await Auth.signOut();
    } catch (err) {
      console.error("Sign out error:", err);
    const msg = typeof err?.message === "string" ? err.message : ""; // guard (as customer portal): a non-string .message made .includes throw and skip the cleanup below
    // Only show error toast for actual errors, not for authentication issues
    if (!msg.includes("not authenticated") && !msg.includes("session has expired")) {
      toast.error("Failed to sign out.");
    }
    }
  
    // Cleanup actions
    window.posthog?.reset();
    localStorage.clear();
    if (reload === true) {
      window.location.reload();
    }
    
    /*
    // Original code block to handle sign out
    try {
      const userData = await Auth.currentAuthenticatedUser();
      const refreshToken = userData.signInUserSession.refreshToken.token;
      const clientId = userData.pool.clientId;
      await API.graphql(
        graphqlOperation(signOut, {
          clientId: clientId,
          token: refreshToken,
        })
      );
      Auth.signOut().then(() => {
        localStorage.clear();
        window.location.reload();
      });
    } catch (err) {
      console.log(err, "err signout");
      window.location.reload();
      toast.error("Failed to sign out.");
    } */
  
};

export const getBase64 = async (file) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);

  return new Promise((reslove, reject) => {
    reader.onload = () => reslove(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

export const getPublicIpv4 = async () => {
  const res = await axios.get("https://geolocation-db.com/json/");
  return res.data.IPv4;
};

export const formatDate = (date) => {
  if (!date) return "";
  const date1 = date instanceof Date ? date.toDateString() : date.split("T")[0];
  return moment(date1).format("MM/DD/YYYY");
};

export const numberWithCommas = (number) => {
  if (number !== null && number !== undefined) {
    let formattedNumber = parseFloat(number).toFixed(2); // Always formats to two decimal places
    formattedNumber = formattedNumber.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    // The following replacement is unnecessary since toFixed(2) guarantees .00 for whole numbers
    // However, if there's a specific case or requirement, additional logic can be added here

    return formattedNumber; // .00 will be present for whole numbers due to toFixed(2)
  }
  return null; // or any fallback value you see fit
};

export const formatUSNumber = (number) => {
  if (number) {
    number = parseFloat(number);
    return new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 2,
      style: "currency",
      currency: "USD",
    }).format(number);
  } else {
    return "$" + 0;
  }
};
export function capitalize(s) {
  return s[0].toUpperCase() + s.slice(1);
}

export const colorTextButton = (val) => {
  switch (val) {
    case "PENDING":
      return "#FFFFFF";
    case "COMPLETED":
      return "#FFFFFF";
    default:
      return "#FFFFFF";
  }
};
export const colorBackgroundButton = (val) => {
  switch (val) {
    case "PENDING":
      return "#1646AA";
    case "COMPLETED":
      return "#4FB980";
    case "SCHEDULED":
      return "#FFFFFF";
    default:
      return "#FFFFFF";
  }
};
export const colorBorderButton = (val) => {
  switch (val) {
    case "PENDING":
      return "#4FB980";
    case "COMPLETE":
      return "#4FB980";
    default:
      return "red";
  }
};
export const statusStyles = (val, transactionType) => {
  if (val === "COMPLETED" && transactionType === "DEBIT") {
    return { styles: "payment-border red", text: "Charge" };
  }
  switch (val) {
    case "PENDING":
      return { styles: "payment-border yellow", text: "Pending" };
    case "COMPLETED":
      return { styles: "payment-border green", text: "Paid" };
    case "CANCELLED":
      return { styles: "payment-border red", text: "Cancelled" };
    case "FAILED":
      return { styles: "payment-border red", text: "Failed" };
    case "SCHEDULED":
      return { styles: "payment-border gray", text: "Scheduled" };
    case "Verified":
      return { styles: "payment-border green-bordered", text: "Verified" };
    case "Default":
      return { styles: "payment-border gray", text: "Default" };
    case "Unverified":
      return { styles: "payment-border red", text: "Unverified" };
    case "AUTOMATIC":
      return { styles: "payment-border green-bordered", text: "Scheduled Autopay" };
    default:
      return { styles: "payment-border", text: val };
  }
};
export const getMemberStatementId = () => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const memberStatementId = urlSearchParams.get("memberStatementId");
  return memberStatementId || null;
};

// Function to compress an image file with dynamic quality
export const compressImage = (file) => {
  const isImage = file?.type?.startsWith("image/");
  if (!isImage) {
    return file; // Return the original file if it is not an image
  }

  return new Promise((resolve, reject) => {
    // Check if the file is an image
    // Initial quality
    let quality = 0.9;

    // Maximum dimensions
    const maxWidth = 1920; // Adjust as needed
    const maxHeight = 1080; // Adjust as needed

    // Function to start compression
    const startCompression = () => {
      new Compressor(file, {
        quality: quality,
        maxWidth: maxWidth,
        maxHeight: maxHeight,
        success: (compressedFile) => {
          // Debugging information
          // Check the size of the compressed file
          if (compressedFile.size > 3.5 * 1024 * 1024) {
            quality -= 0.2;
            if (quality < 0.1) {
              reject(new Error("Cannot compress file below 3.5 MB"));
            } else {
              startCompression(); // Retry compression with lower quality
            }
          } else {
            resolve(compressedFile);
          }
        },
        error: (error) => reject(error),
      });
    };

    startCompression();
  });
};

// Maps Payabli's hosted-page redirect result to a toast. Extracted so the approved/not-approved decision is unit-testable without mounting Dashboard; "Approved" is verified against a live redirect (resultCode=1&resultText=Approved), so any other value is genuinely not approved.
export const payabliResultToast = (resultText) => {
  const result = resultText?.trim();
  if (result === "Approved") {
    return { type: "success", message: "Your Payabli payment was successful! Your payment should appear shortly" };
  }
  // Generic on purpose: Payabli does not document the non-approved vocabulary, and the values it
  // does return are processor jargon ("CVV2/CVC2 match", "No Match, No address or ZIP match"),
  // so the raw resultText is not tenant-facing copy.
  return {
    type: "error",
    message: "Your Payabli payment was not completed. Please check your payment status before trying again.",
  };
};
